import { AfterViewInit, Component, DestroyRef, OnInit } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { getDateformatMonthYearString, getNextMonthDate } from 'src/app/apv/core/utils/functions';
import { toClp } from '../../../core/utils/formatters';
import { Util } from 'src/app/shared/common/utils/util';
import {
  SHELL_HOME_URL,
  SHELL_PERSONAL_DATA_URL,
} from '../../../core/shell/shell-routes.constant';
import { SUBSCRIPTION_URLS } from '../../constants/subscription-routes.constant';
import { SUBSCRIPTION_STORAGE_KEYS } from '../../constants/subscription-storage.constant';
import { SUCCESS_LABELS, SUCCESS_TEXTS } from '../../constants/success.constant';
import { IDetailRow } from '../../models/subscription.model';
import { FundCatalogService } from '../../services/fund-catalog.service';

@Component({
    selector: 'app-success-page',
    templateUrl: './success.page.html',
    styleUrls: ['./success.page.scss'],
    standalone: false
})
export class SuccessPage implements OnInit, AfterViewInit {
  readonly texts = SUCCESS_TEXTS;
  readonly personalDataUrl = SHELL_PERSONAL_DATA_URL;

  voucherDetails: IDetailRow[] = [];
  dateDetails: IDetailRow[] = [];
  fundDetails: IDetailRow[] = [];

  public subscription: any = {};

  private readonly nextMonth: Date = getNextMonthDate(new Date());
  private readonly nextSecondMonth: Date = getNextMonthDate(this.nextMonth);

  constructor(
    private readonly router: Router,
    private readonly util: Util,
    private readonly fundCatalog: FundCatalogService,
    private readonly destroyRef: DestroyRef
  ) {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart =>
            event instanceof NavigationStart &&
            event.navigationTrigger === 'popstate'
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => this.redirectToHome());
  }

  ngAfterViewInit() {
    const botDiv = document.getElementById('botDiv');
    const content = document.querySelector('.success-container');

    if (botDiv && content) {
      content.classList.add('with-bot');
    }
  }

  ngOnInit() {
    this.subscription = JSON.parse(
      String(sessionStorage.getItem(SUBSCRIPTION_STORAGE_KEYS.result))
    );

    if (!this.subscription) this.redirectToHome();

    this.buildDetails();
    this.util.levantarEncuesta('Suscripcion-APV');
  }

  private buildDetails(): void {
    this.voucherDetails = [
      { label: SUCCESS_LABELS.folio, value: this.subscription?.folio ?? '' },
      { label: SUCCESS_LABELS.monthlyAmount, value: this.savesBySubscription() },
    ];

    this.dateDetails = [
      { label: SUCCESS_LABELS.subscriptionDate, value: this.formatDate(new Date()) },
      {
        label: SUCCESS_LABELS.firstDiscount,
        value: getDateformatMonthYearString(this.nextMonth),
      },
      {
        label: SUCCESS_LABELS.savingStart,
        value: getDateformatMonthYearString(this.nextSecondMonth),
      },
    ];

    this.fundCatalog
      .fundName(this.subscription?.fundTypeDestination)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((fundName) => {
        this.fundDetails = [
          { label: SUCCESS_LABELS.fund, value: fundName },
          { label: SUCCESS_LABELS.regime, value: this.subscription?.taxRegimeCode ?? '' },
        ];
      });
  }

  private redirectToHome() {
    this.router.navigate([SUBSCRIPTION_URLS.accountDetail]);
  }

  private formatDate(date: Date): string {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }

  private savesBySubscription(): string {
    return this.currencyByAmountAndCurrencyCode(
      this.subscription?.discountValue,
      this.subscription?.discountType
    );
  }

  private currencyByAmountAndCurrencyCode(
    amount: number,
    currencyCode: string
  ): string {
    switch (currencyCode) {
      case 'P':
        return `$ ${this.numberToCurrency(amount)}`;
      case 'U':
        return `${this.numberToCurrency(amount)} UF`;
      default:
        return '';
    }
  }

  private numberToCurrency(number: number): string {
    return number ? toClp(number) : '';
  }

  get hasEmail(): boolean {
    return !!this.subscription?.email;
  }

  get hasVoucher(): boolean {
    return !!this.subscription?.pdf?.content;
  }

  public goHome(): void {
    this.router.navigate([SHELL_HOME_URL]);
  }

  public downloadMyFile() {
    if (!this.hasVoucher) return;

    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute(
      'href',
      `data:application/pdf;base64,${this.subscription.pdf.content}`
    );
    link.setAttribute('download', `comprobante_apv.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}
